import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Col, Row, Button } from 'reactstrap';
import DatePicker from 'react-datepicker';
import ReactLoading from 'react-loading';
import { get } from 'lodash-es';
import moment from 'moment-timezone';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAxios } from '../../../hooks';
import { selectHouseRef } from '../../../selectors';
import { invoicesPath } from '../../../utilities/UrlPaths';
import ExistingPaging from './ExistingPaging';

const blockEl = 'invoices__existing-invoices';

const ExistingInvoices = () => {
  const [invoiceGroups, setInvoiceGroups] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [apiDateString, setApiDateString] = useState('');
  const [isFilteringDates, setIsFilteringDates] = useState(false);
  const [illegalDateRange, setIllegalDateRange] = useState(false);
  const [showClearDates, setShowClearDates] = useState(false);
  const groupsPerPage = 10;
  const [startRowIndex, setStartRowIndex] = useState(0);
  const [endRowIndex, setEndRowIndex] = useState(startRowIndex + groupsPerPage);
  const [totalGroups, setTotalGroups] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [apiPage, setApiPage] = useState(0);
  const [urlDateString, setUrlDateString] = useState('');
  const houseRef = useSelector(selectHouseRef);
  const maxDate = new Date();

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const queryPage = queryParams.get('page') || 0;
    const queryStart = queryParams.get('start') || '';
    const queryEnd = queryParams.get('end') || '';
    setCurrentPage(Number(queryPage));
    setApiPage(Number(queryPage));
    if (queryStart) setStartDate(moment(queryStart).toDate());
    if (queryEnd) setEndDate(moment(queryEnd).toDate());
  }, []);

  const { data, isIdle, isLoading } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroups?size=${groupsPerPage}&page=${apiPage}${apiDateString}`,
    []
  );

  const { data: paymentProcessorTypeData } = useAxios(
    () => `/app/house/${houseRef}/paymentProcessorType`,
    [houseRef]
  );

  const processorType = useMemo(() => get(paymentProcessorTypeData, 'data.data.processor-type'), [
    paymentProcessorTypeData
  ]);

  useEffect(() => {
    if (data) {
      setInvoiceGroups(get(data, 'data.data.invoices', []));
      setTotalGroups(get(data, 'data.data.totalRecords', 250));
      setIsFilteringDates(false);
      setCurrentPage(apiPage);
    }
  }, [data]);

  useEffect(() => {
    if (endDate || startDate) {
      setShowClearDates(true);
      const formatStartDate = moment(startDate);
      const formatEndDate = moment(endDate);

      // set date string for pagination
      let paginationDateString = '';
      if (startDate) paginationDateString += `&start=${moment(startDate).format('YYYY-MM-DD')}`;
      if (endDate) paginationDateString += `&end=${moment(endDate).format('YYYY-MM-DD')}`;
      setUrlDateString(paginationDateString);

      let apiDates = '';
      const startBlob = `&startDate=${moment(startDate).format('MM/DD/YYYY')}`;
      const endBlob = `&endDate=${moment(endDate).format('MM/DD/YYYY')}`;
      if (startDate && endDate) {
        apiDates = `${startBlob}${endBlob}`;
      } else if (startDate) {
        apiDates = `${startBlob}&endDate=${moment(new Date()).format('MM/DD/YYYY')}`;
      } else if (endDate) {
        apiDates = `&startDate=01/01/1989${endBlob}`;
      }
      setApiDateString(apiDates);
      //  resets for first page of results
      if (isFilteringDates) {
        setApiPage(0);
        setStartRowIndex(0);
        setEndRowIndex(groupsPerPage);
      }

      if (
        formatStartDate.isValid()
        && formatEndDate.isValid()
        && formatStartDate.isAfter(formatEndDate)
      ) {
        setIllegalDateRange(true);
      }
    } else {
      setUrlDateString('');
      setApiDateString('');
    }
  }, [startDate, endDate]);

  const handleOnClearDates = () => {
    const url = `${invoicesPath}/ExistingInvoices`;
    window.location.href = url;
  };

  useEffect(() => {
    setStartRowIndex(currentPage * groupsPerPage);
    setEndRowIndex(currentPage * groupsPerPage + groupsPerPage);
  }, [currentPage]);

  if (isIdle || isLoading) {
    return <ReactLoading className="loading-spinner" type="spin" color="black" />;
  }

  return (
    <Fragment>
      <Row>
        <Col xs={12} className="font-weight-bold mt-2">
          Date Range
        </Col>
      </Row>
      <Row>
        <Col xs={8}>
          <div className={`${blockEl}__date-box`}>
            <DatePicker
              id="startDate"
              autoComplete="off"
              className="p-2"
              maxDate={maxDate}
              placeholderText="From"
              selected={startDate}
              onChange={date => {
                setIsFilteringDates(true);
                setStartDate(date);
              }}
              popperPlacement="bottom-start"
            />
          </div>
          <div className={`${blockEl}__date-box`}>
            <DatePicker
              id="endDate"
              autoComplete="off"
              className="p-2"
              maxDate={maxDate}
              placeholderText="To"
              selected={endDate}
              onChange={date => {
                setIsFilteringDates(true);
                setEndDate(date);
              }}
              popperPlacement="bottom-start"
            />
          </div>
          {showClearDates && (
            <Button
              className="ml-4 d-inline-block"
              aria-label="clear dates"
              color="link"
              onClick={handleOnClearDates}
            >
              Clear Dates
            </Button>
          )}
        </Col>
        <Col
          xs={4}
          className="mt-3 mr-0"
          style={{
            justifyContent: 'flex-end'
          }}
        >
          {invoiceGroups.length > 0 && (
            <div>
              <ExistingPaging
                totalGroups={totalGroups}
                startRowIndex={startRowIndex}
                currentPage={currentPage}
                urlDateString={urlDateString}
                endRowIndex={endRowIndex}
              />
            </div>
          )}
        </Col>
      </Row>

      {!invoiceGroups.length && (
        <Row className="mt-4">
          <Col xs={12}>
            <div>
              {illegalDateRange ? (
                <div className="text-danger mt-n3">TO date cannot be before FROM date</div>
              ) : (
                'No invoices available'
              )}
            </div>
          </Col>
        </Row>
      )}

      {invoiceGroups.map(group => (
        <Row className="mt-4 mb-5" key={group.invoiceGroupID}>
          <Col xs={12}>
            <div className="auction-container w-100 font-weight-bold px-3">
              <div className="float-start"> {group.invoiceGroupTitle}</div>
              <div className="float-end text-end">
                {get(group, 'invoiceCreated', true) ? (
                  <>
                    <a
                      aria-label={`template ${group.invoiceGroupSettingsID}`}
                      className="link-secondary me-3"
                      href={`${window.location.origin}/ar/invoices/invoiceTemplates/${group.invoiceGroupSettingsID}`}
                    >
                      Edit Template
                    </a>
                    |
                    <Link
                      aria-label={`viewedit ${group.invoiceGroupSettingsID}`}
                      className="link-secondary ms-3 me-3"
                      to={`/invoices/InvoiceGroupDetails/${group.invoiceGroupID}`}
                    >
                      View Invoices
                    </Link>
                    |
                    <Link
                      aria-label={`viewedit ${group.invoiceGroupSettingsID}`}
                      className="link-secondary ms-3"
                      to={`/invoices/EditInvoices/${group.invoiceGroupID}`}
                    >
                      Edit Invoices
                    </Link>
                  </>
                ) : (
                  <p>
                    <FontAwesomeIcon className="mr-1" icon="info-circle" />
                    Invoice creation is in Progress. Please refresh page for updated status
                  </p>
                )}
              </div>
            </div>
          </Col>
          <Col xs={12}>
            <div className="col-3 float-start font-weight-bold border px-3 py-2 bottom-shadow">
              Auction Date
            </div>
            <div className="col-9 float-start font-weight-bold border border-left-none px-3 py-2 bottom-shadow">
              Sale Title
            </div>
          </Col>
          {group.catalogs
            && group.catalogs.map(catalog => (
              <Col xs={12} key={catalog.catalogID}>
                <div className="border border-top-none">
                  <Row>
                    <Col xs={12}>
                      <div className="col-3 float-start border-right px-3 py-2">{catalog.date}</div>
                      <div className="col-9 float-start px-3 py-2">{catalog.title}</div>
                    </Col>
                  </Row>
                </div>
              </Col>
            ))}
        </Row>
      ))}
      {invoiceGroups.length > 0 && (
        <div className="mb-5">
          <ExistingPaging
            totalGroups={totalGroups}
            startRowIndex={startRowIndex}
            currentPage={currentPage}
            urlDateString={urlDateString}
            endRowIndex={endRowIndex}
          />
        </div>
      )}
    </Fragment>
  );
};

export default ExistingInvoices;
